"use client";

import {
  useState,
  useContext,
  useEffect,
  useCallback,
  useReducer,
  useRef,
  Suspense,
} from "react";
import { useRouter, useSearchParams } from "next/navigation";

import {
  AbsoluteCenter,
  Box,
  Button,
  Input,
  Heading,
  VStack,
  Field,
  Checkbox,
} from "@chakra-ui/react";

import { AuthContext } from "@/app/providers/AuthProvider";

import { useShallow } from "zustand/react/shallow";
import useStore from "@/lib/sessionStore";

import CustomPINInput from "@/components/CustomPINInput";

function LoginContent() {
  const router = useRouter();
  const searchParams = useSearchParams();
  const redirect = searchParams.get("redirect") || "/dashboard";
  const { user, loaded } = useContext(AuthContext);
  const { signIn, confirmSignIn, usernameAvailable } = useStore(
    useShallow((state) => ({
      signIn: state.signIn,
      confirmSignIn: state.confirmSignIn,
      usernameAvailable: state.usernameAvailable,
    }))
  );

  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    {
      username: "",
      loginName: "",
      showPinInput: false,
    }
  );

  const [error, setError] = useState("");
  const usernameInputRef = useRef(null);
  const pinInputRef = useRef(null);
  const [rememberMe, setRememberMe] = useState(false);

  useEffect(() => {
    const savedUsername = localStorage.getItem("prifinaAdmin_username");
    if (savedUsername) {
      setState({ loginName: savedUsername });
      setRememberMe(true);
    }
  }, []);

  useEffect(() => {
    if (loaded && user) {
      router.push(redirect);
    }
  }, [loaded, user, router, redirect]);

  const [pinError, setPinError] = useState("");
  const [isBusy, setIsBusy] = useState(false);
  const verify = useCallback(
    async (code) => {
      const confirmSignInResponse = await confirmSignIn(code);
      if (confirmSignInResponse) {
        window.location.href = redirect;
      }
    },
    [confirmSignIn, redirect]
  );

  const validateUsername = useCallback(() => {
    if (!state.loginName.trim()) {
      setError("Enter Username");
      return false;
    }
    setError("");
    return true;
  }, [state.loginName]);

  const handlePinPointerDownCapture = useCallback(
    (e) => {
      if (!validateUsername()) {
        e.preventDefault();
        e.stopPropagation();
        setTimeout(() => usernameInputRef.current?.focus(), 0);
      }
    },
    [validateUsername]
  );

  const handleNext = useCallback(async () => {
    if (!validateUsername()) return;

    if (rememberMe) {
      localStorage.setItem("prifinaAdmin_username", state.loginName);
    } else {
      localStorage.removeItem("prifinaAdmin_username");
    }

    setIsBusy(true);
    const userDoesNotExists = await usernameAvailable(state.loginName);
    if (!userDoesNotExists) {
      const signInResponse = await signIn(state.loginName);
      if (signInResponse) {
        setState({ showPinInput: true });
      } else {
        setError("Sign in failed");
      }
    } else {
      setError("Username does not exists");
      usernameInputRef.current?.focus();
    }
    setIsBusy(false);
  }, [usernameAvailable, validateUsername, rememberMe, state.loginName, signIn]);

  /* 
  login: {
    title: "Login",
    usernameLabel: "Username",
    usernamePlaceholder: "e.g., john_doe",
    usernameHelper: "Enter your username, email, or AI-name",
    checkingText: "Checking...",
    nextButton: "Next",
    pinLabel: "Enter PIN",
    pinHelper: "Enter your authenticator PIN code",
    otpHelper: "Enter your email OTP code",
    backButton: "Back",
    needHelp: "Need help?",
    noAccount: "Don't have an account?",
  }, */

  return (
    <AbsoluteCenter>
      <Box
        w={"450px"}
        mx="auto"
        mt={10}
        p={8}
        borderWidth="1px"
        borderRadius="lg"
      >
        <Heading mb={6}>Login</Heading>
        <VStack spacing={4} align="left" mt={8}>
          <Field.Root invalid={!!error}>
            <Field.Label>{"Username"}</Field.Label>
            <Input
              ref={usernameInputRef}
              placeholder={"e.g., john_doe"}
              value={state.loginName}
              onChange={(e) => {
                setState({ loginName: e.target.value });
                if (error) setError(""); // Clear error when user starts typing
              }}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  handleNext();
                }
              }}
              autoComplete="username"
            />
            {error ? (
              <Field.ErrorText>{error}</Field.ErrorText>
            ) : (
              <Field.HelperText>{"Enter your username"}</Field.HelperText>
            )}
          </Field.Root>
          <Field.Root invalid={!!error}>
            <Field.Label>{"Enter PIN"}</Field.Label>
            <CustomPINInput
              ref={pinInputRef}
              verify={verify}
              isBusy={isBusy}
              setIsBusy={setIsBusy}
              reset={() => setPinError("")}
              onFocus={handleNext}
              onPointerDownCapture={handlePinPointerDownCapture}
            />
            {error ? (
              <Field.ErrorText>{pinError}</Field.ErrorText>
            ) : (
              <Field.HelperText>
                {"Enter your authenticator PIN code"}
              </Field.HelperText>
            )}
          </Field.Root>
          <Button
            onClick={handleNext}
            loading={isBusy}
            loadingText="Verifying..."
            colorPalette="teal"
            variant="solid"
            size="lg"
            width="full"
          >
            Login
          </Button>
          <Checkbox.Root
            checked={rememberMe}
            onCheckedChange={(e) => {
              setRememberMe(e.checked);
              if (e.checked && state.loginName.trim()) {
                localStorage.setItem("prifinaAdmin_username", state.loginName);
              } else {
                localStorage.removeItem("prifinaAdmin_username");
              }
            }}
            disabled={!state.loginName.trim()}
            colorPalette="teal"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label>Remember me</Checkbox.Label>
          </Checkbox.Root>
        </VStack>
      </Box>
    </AbsoluteCenter>
  );
}

export default function LoginPage() {
  return (
    <Suspense fallback={<div>Loading...</div>}>
      <LoginContent />
    </Suspense>
  );
}
